/**
 * Chargement des images référencées par les exports Word / PDF.
 *
 * Le convertisseur HTML→Markdown (voir NoteExportMenu.htmlToMarkdown) émet
 * les <img> sur leur propre ligne avec un marqueur neutre :
 *
 *   [[IMG|<url-encoded src>|alt|w|h]]
 *
 * (w/h sont facultatifs — 0 = "prendre la taille naturelle de l'image").
 *
 * Ce module :
 *   1. Repère toutes les URLs d'images dans un markdown.
 *   2. Les télécharge en parallèle → Uint8Array (Word) + dataURL (PDF).
 *   3. Détermine leur format et leur taille naturelle.
 *
 * Les exporteurs Word / PDF acceptent ensuite la Map résultante et rendent
 * chaque image à sa position exacte dans le flux.
 */

export type ImageFormat = "png" | "jpg" | "gif" | "bmp" | "svg";

export interface LoadedImage {
  /** Buffer binaire (docx ImageRun.data). */
  data: Uint8Array;
  /** Data URL (jsPDF addImage). */
  dataUrl: string;
  /** MIME du blob téléchargé. */
  mime: string;
  /** Taille naturelle en pixels (0 si indéterminée). */
  width: number;
  height: number;
  /** Format normalisé pour docx (`type`) / jsPDF (`format`). */
  format: ImageFormat;
}

export type ImageMap = Map<string, LoadedImage>;

const IMG_LINE_RE =
  /^\[\[IMG\|([^|]*)\|([^|]*)\|([^|]*)\|([^|\]]*)\]\]$/;

/** Extrait les infos d'un marqueur `[[IMG|src|alt|w|h]]` (ou null). */
export function parseImageMarker(
  line: string,
): { src: string; alt: string; w: number; h: number } | null {
  const m = line.trim().match(IMG_LINE_RE);
  if (!m) return null;
  let src = "";
  try {
    src = decodeURIComponent(m[1]);
  } catch {
    src = m[1];
  }
  return {
    src,
    alt: m[2] || "",
    w: Math.max(0, parseInt(m[3], 10) || 0),
    h: Math.max(0, parseInt(m[4], 10) || 0),
  };
}

/** Détecte si une ligne est un marqueur image (sans le décoder entièrement). */
export function isImageMarkerLine(line: string): boolean {
  return IMG_LINE_RE.test(line.trim());
}

/** Ensemble ordonné des URLs d'images à charger pour un markdown donné. */
export function collectImageSources(markdown: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of markdown.split("\n")) {
    const p = parseImageMarker(raw);
    if (!p || !p.src) continue;
    if (seen.has(p.src)) continue;
    seen.add(p.src);
    out.push(p.src);
  }
  return out;
}

function detectFormat(mime: string, src: string): ImageFormat {
  const m = mime.toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("gif")) return "gif";
  if (m.includes("bmp")) return "bmp";
  if (m.includes("svg")) return "svg";
  const ext = (src.split("?")[0].split("#")[0].split(".").pop() || "").toLowerCase();
  if (ext === "png" || ext === "gif" || ext === "bmp" || ext === "svg") return ext;
  if (ext === "jpg" || ext === "jpeg") return "jpg";
  return "png";
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });
}

function probeNaturalSize(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
      img.onerror = () => resolve({ width: 0, height: 0 });
      img.src = dataUrl;
    } catch {
      resolve({ width: 0, height: 0 });
    }
  });
}

/** Charge une image (fetch + conversion). Renvoie null en cas d'échec. */
export async function loadImage(src: string): Promise<LoadedImage | null> {
  try {
    const res = await fetch(src, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const buf = new Uint8Array(await blob.arrayBuffer());
    const dataUrl = src.startsWith("data:") ? src : await blobToDataUrl(blob);
    const nat = await probeNaturalSize(dataUrl);
    return {
      data: buf,
      dataUrl,
      mime: blob.type || "",
      width: nat.width,
      height: nat.height,
      format: detectFormat(blob.type || "", src),
    };
  } catch {
    return null;
  }
}

/** Précharge toutes les images référencées dans un markdown. */
export async function preloadImagesForMarkdown(markdown: string): Promise<ImageMap> {
  const srcs = collectImageSources(markdown);
  const out: ImageMap = new Map();
  await Promise.all(
    srcs.map(async (src) => {
      const img = await loadImage(src);
      if (img) out.set(src, img);
    }),
  );
  return out;
}
